import { useFirstWeekMilestones } from '../hooks/useFirstWeekMilestones.js'

// First-week activation banner on Daily Huddle.
//
// Renders only when:
//   - Restaurant signed up < 14 days ago
//   - At least one milestone is incomplete
//   - Customer hasn't dismissed
//
// Each row has a check (or empty circle) on the left, label + sub
// on the middle, and a tab-switch CTA on the right. Tapping the CTA
// calls onSwitchTab(tab) which the parent (DailyHuddleScreen) wires
// to the App-level navigation function.
//
// Styling: clean blue card, not loud. Goal is "helpful nudge" not
// "screaming onboarding wizard."
export default function FirstWeekMilestonesCard({ onSwitchTab }) {
  const { show, milestones, daysSinceSignup, dismiss } = useFirstWeekMilestones()
  if (!show) return null

  const completed = milestones.filter((m) => m.complete).length

  return (
    <section
      className="mx-4 mt-3 rounded-2xl border border-blue-200 bg-blue-50/60 p-4 shadow-sm"
      aria-label="First-week setup checklist"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-medium text-white">
              Setup
            </span>
            <h3 className="text-base font-medium text-blue-900">
              Get the most out of AI Restaurant Manager
            </h3>
          </div>
          <p className="mt-1 text-xs text-blue-900/70">
            {completed} of {milestones.length} done
            {daysSinceSignup != null && ` · ${Math.max(0, 14 - daysSinceSignup)} days left in your trial`}
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="rounded-md px-2 py-1 text-xs font-medium text-blue-900/55 hover:text-blue-900 hover:bg-blue-100"
          aria-label="Hide setup checklist"
        >
          Hide
        </button>
      </div>

      <ul className="mt-3 space-y-2">
        {milestones.map((m) => (
          <li
            key={m.id}
            className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${
              m.complete
                ? 'border-emerald-200 bg-emerald-50/60'
                : 'border-blue-200 bg-white'
            }`}
          >
            <span
              aria-hidden="true"
              className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-medium ${
                m.complete
                  ? 'bg-emerald-600 text-white'
                  : 'bg-blue-100 text-blue-600 ring-1 ring-inset ring-blue-300'
              }`}
            >
              {m.complete ? '✓' : ''}
            </span>
            <div className="min-w-0 flex-1">
              <div className={`text-sm font-medium ${m.complete ? 'text-emerald-900 line-through decoration-emerald-400/60 decoration-1' : 'text-blue-900'}`}>
                {m.label}
              </div>
              <div className="text-xs text-blue-900/65">{m.sub}</div>
            </div>
            {!m.complete && m.cta && (
              <button
                type="button"
                onClick={() => onSwitchTab?.(m.cta.tab)}
                className="flex-shrink-0 rounded-md border border-blue-300 bg-white px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
              >
                {m.cta.label}
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}
